import { useState } from 'react';
import type { MarkLevel, MarkPoint, SeedDistractor, AiSuggestion } from './api.js';

/** A status/quality chip. */
export function Chip({ kind, label }: { kind: string; label: string }) {
  return <span className={`chip ${kind.toLowerCase()}`}>{label}</span>;
}

// ---------------------------------------------------------------------------
// M9 — two-axis labelled chips.
//
// The SME console previously rendered three same-shaped chips per row with
// no axis labels (e.g. `[GENUINE][DRAFT][pending]`) and SMEs couldn't tell
// which was the lifecycle vs. the verdict vs. the origin. M9 introduces
// THREE wrapped components with explicit `Axis: value` labels and visually
// distinct treatments:
//
//   LifecycleChip      — solid fill,    label "Lifecycle: DRAFT|ACTIVE|RETIRED"
//   ReviewVerdictChip  — outlined,      label "Review: pending|approved|rejected"
//   OriginChip         — small ◆ icon,  label "Origin: GENUINE|SYNTHETIC"
//
// Color schemes are defined in review/styles.css under .chip.lifecycle-*,
// .chip.review-*, .chip.origin-*. We deliberately KEEP the existing
// ReviewChip + QualityChip (re-implemented in terms of the new chips) so
// the smoke pre-deploy keeps passing while the client rebuild propagates,
// and so any future callsite that imports the old name still renders the
// improved chip.
// ---------------------------------------------------------------------------

export type Lifecycle = 'DRAFT' | 'ACTIVE' | 'RETIRED';
export type ReviewVerdict = 'pending' | 'approved' | 'rejected';
export type Origin = 'GENUINE' | 'SYNTHETIC';

function normalizeLifecycle(s: string): Lifecycle {
  const u = (s || '').toUpperCase();
  return u === 'ACTIVE' || u === 'RETIRED' ? (u as Lifecycle) : 'DRAFT';
}

function normalizeVerdict(s: string): ReviewVerdict {
  const l = (s || '').toLowerCase();
  return l === 'approved' || l === 'rejected' ? (l as ReviewVerdict) : 'pending';
}

function normalizeOrigin(s: string): Origin {
  return (s || '').toUpperCase() === 'SYNTHETIC' ? 'SYNTHETIC' : 'GENUINE';
}

export function LifecycleChip({
  status,
  size,
  showLabel = true,
}: {
  status: string;
  size?: 'sm';
  showLabel?: boolean;
}) {
  const v = normalizeLifecycle(status);
  const cls = `chip lifecycle-${v.toLowerCase()}${size === 'sm' ? ' chip-sm' : ''}`;
  return (
    <span className={cls} data-testid={`chip-lifecycle-${v.toLowerCase()}`}>
      {showLabel ? 'Lifecycle: ' : ''}
      {v}
    </span>
  );
}

export function ReviewVerdictChip({
  status,
  size,
  showLabel = true,
}: {
  status: string;
  size?: 'sm';
  showLabel?: boolean;
}) {
  const v = normalizeVerdict(status);
  const cls = `chip review-${v}${size === 'sm' ? ' chip-sm' : ''}`;
  return (
    <span className={cls} data-testid={`chip-review-${v}`}>
      {showLabel ? 'Review: ' : ''}
      {v}
    </span>
  );
}

export function OriginChip({
  quality,
  size,
  showLabel = true,
}: {
  quality: string;
  size?: 'sm';
  showLabel?: boolean;
}) {
  const v = normalizeOrigin(quality);
  const cls = `chip origin-${v.toLowerCase()}${size === 'sm' ? ' chip-sm' : ''}`;
  return (
    <span className={cls} data-testid={`chip-origin-${v.toLowerCase()}`}>
      <span className="chip-icon" aria-hidden="true">◆ </span>
      {showLabel ? 'Origin: ' : ''}
      {v}
    </span>
  );
}

// ---- Back-compat shims ----------------------------------------------------
// Existing call sites import `ReviewChip` and `QualityChip`. We keep these
// names but route them through the new labelled chips so the UI updates
// uniformly. (Old, unlabelled callsites are M8-and-earlier code we have not
// touched in this patch — they all live in the review/ folder and will pick
// up the new look on the next client rebuild.)

export function ReviewChip({ status }: { status: string }) {
  return <ReviewVerdictChip status={status} />;
}

export function QualityChip({ quality }: { quality: string }) {
  return <OriginChip quality={quality} />;
}

/** Editable list of plain string rows (add / edit / remove). */
export function StringListEditor({
  values,
  onChange,
  placeholder,
  testidPrefix,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  testidPrefix: string;
}) {
  return (
    <div className="row-list">
      {values.map((v, i) => (
        <div className="inline-row" key={i}>
          <textarea
            value={v}
            placeholder={placeholder}
            onChange={(e) => {
              const next = values.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`input-${testidPrefix}-${i}`}
          />
          <button
            className="ghost sm"
            type="button"
            title="Remove"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            data-testid={`button-remove-${testidPrefix}-${i}`}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        className="ghost sm"
        type="button"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => onChange([...values, ''])}
        data-testid={`button-add-${testidPrefix}`}
      >
        + Add
      </button>
    </div>
  );
}

/** Editable list of {agent, example} application rows. */
export function ApplicationsEditor({
  values,
  onChange,
}: {
  values: { agent: string; example: string }[];
  onChange: (next: { agent: string; example: string }[]) => void;
}) {
  return (
    <div className="row-list">
      {values.map((v, i) => (
        <div className="inline-row" key={i}>
          <input
            style={{ maxWidth: 140 }}
            value={v.agent}
            placeholder="agent"
            onChange={(e) => {
              const next = values.slice();
              next[i] = { ...next[i], agent: e.target.value };
              onChange(next);
            }}
            data-testid={`input-application-agent-${i}`}
          />
          <textarea
            value={v.example}
            placeholder="example"
            onChange={(e) => {
              const next = values.slice();
              next[i] = { ...next[i], example: e.target.value };
              onChange(next);
            }}
            data-testid={`input-application-example-${i}`}
          />
          <button
            className="ghost sm"
            type="button"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            data-testid={`button-remove-application-${i}`}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        className="ghost sm"
        type="button"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => onChange([...values, { agent: '', example: '' }])}
        data-testid="button-add-application"
      >
        + Add application
      </button>
    </div>
  );
}

/** Edit a JSON object via a raw textarea, with parse validation. */
export function JsonEditor({
  value,
  onChange,
  testid,
}: {
  value: Record<string, unknown> | null;
  onChange: (next: Record<string, unknown>) => void;
  testid: string;
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);
  return (
    <div>
      <textarea
        style={{ minHeight: 120, fontFamily: 'var(--mono)' }}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            setErr(null);
            onChange(parsed);
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
        data-testid={testid}
      />
      {err && <div className="err">Invalid JSON: {err}</div>}
    </div>
  );
}

// ---- typed question-editor widgets (spec §5) -----------------------------

/**
 * Read-only level band row. Level bands are canonical (tier-wide, PM-locked) and are
 * edited only in levelRubricFor — never by the SME here. Marks + descriptor shown for
 * judgment context.
 */
export function LevelRow({ level }: { level: MarkLevel }) {
  return (
    <tr data-testid={`level-row-${level.level}`}>
      <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>L{level.level}</td>
      <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{level.marks}</td>
      <td>{level.descriptor}</td>
    </tr>
  );
}

/** Editable points-scheme row: marks (number) + criterion (text). */
export function PointRow({
  point,
  onChange,
  onRemove,
  testid,
}: {
  point: MarkPoint;
  onChange: (next: MarkPoint) => void;
  onRemove: () => void;
  testid: string;
}) {
  return (
    <div className="inline-row" data-testid={testid}>
      <input
        type="number"
        style={{ maxWidth: 70 }}
        value={String(point.marks)}
        onChange={(e) => onChange({ ...point, marks: Number(e.target.value) })}
        data-testid={`${testid}-marks`}
      />
      <textarea
        value={point.criterion}
        placeholder="criterion"
        onChange={(e) => onChange({ ...point, criterion: e.target.value })}
        data-testid={`${testid}-criterion`}
      />
      <button className="ghost sm" type="button" title="Remove" onClick={onRemove} data-testid={`${testid}-remove`}>
        ✕
      </button>
    </div>
  );
}

/** Bullet-list editor for indicative content (add / edit / remove / reorder). */
export function IndicativeList({
  values,
  onChange,
  testidPrefix,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  testidPrefix: string;
}) {
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= values.length) return;
    const next = values.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="row-list">
      {values.map((v, i) => (
        <div className="inline-row" key={i}>
          <textarea
            value={v}
            placeholder="indicative content point"
            onChange={(e) => {
              const next = values.slice();
              next[i] = e.target.value;
              onChange(next);
            }}
            data-testid={`input-${testidPrefix}-${i}`}
          />
          <button className="ghost sm" type="button" title="Move up" onClick={() => move(i, -1)} data-testid={`button-up-${testidPrefix}-${i}`}>
            ↑
          </button>
          <button className="ghost sm" type="button" title="Move down" onClick={() => move(i, 1)} data-testid={`button-down-${testidPrefix}-${i}`}>
            ↓
          </button>
          <button
            className="ghost sm"
            type="button"
            title="Remove"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            data-testid={`button-remove-${testidPrefix}-${i}`}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        className="ghost sm"
        type="button"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => onChange([...values, ''])}
        data-testid={`button-add-${testidPrefix}`}
      >
        + Add point
      </button>
    </div>
  );
}

/** MCQ options editor: radio = correct, option text, "why wrong" for the distractors. */
export function McqOptions({
  options,
  onChange,
  testidPrefix,
}: {
  options: SeedDistractor[];
  onChange: (next: SeedDistractor[]) => void;
  testidPrefix: string;
}) {
  const setCorrect = (i: number) =>
    onChange(options.map((o, j) => ({ ...o, is_correct: j === i })));
  const patch = (i: number, p: Partial<SeedDistractor>) =>
    onChange(options.map((o, j) => (j === i ? { ...o, ...p } : o)));
  return (
    <div className="row-list">
      {options.map((o, i) => (
        <div className="inline-row" key={i} data-testid={`${testidPrefix}-${i}`} style={{ alignItems: 'flex-start' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
            <input
              type="radio"
              name={testidPrefix}
              checked={o.is_correct}
              onChange={() => setCorrect(i)}
              data-testid={`${testidPrefix}-${i}-correct`}
            />
            <span style={{ fontFamily: 'var(--mono)' }}>{o.option}</span>
          </label>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <textarea
              value={o.text}
              placeholder="option text"
              onChange={(e) => patch(i, { text: e.target.value })}
              data-testid={`${testidPrefix}-${i}-text`}
            />
            {!o.is_correct && (
              <textarea
                value={o.why_wrong ?? ''}
                placeholder="why wrong"
                onChange={(e) => patch(i, { why_wrong: e.target.value })}
                data-testid={`${testidPrefix}-${i}-why`}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Render any value (string / string[] / objects) as readable text for a diff cell. */
function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v
      .map((x) =>
        typeof x === 'string'
          ? `• ${x}`
          : `• ${JSON.stringify(x)}`,
      )
      .join('\n');
  }
  return JSON.stringify(v, null, 2);
}

/** Inline current-vs-suggested diff for one AI suggestion, with Accept / Reject. */
export function SuggestionDiff({
  suggestion,
  onAccept,
  onReject,
  testid,
}: {
  suggestion: AiSuggestion;
  onAccept: () => void;
  onReject: () => void;
  testid: string;
}) {
  return (
    <div className="suggestion-diff" data-testid={testid}>
      <div className="suggestion-field" style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>
        {suggestion.field}
      </div>
      {suggestion.rationale && <div className="muted">{suggestion.rationale}</div>}
      <div className="diff-cols" style={{ display: 'flex', gap: 8 }}>
        <pre className="diff-current" style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
          {renderValue(suggestion.current)}
        </pre>
        <pre className="diff-suggested" style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
          {renderValue(suggestion.suggested)}
        </pre>
      </div>
      <div className="row-actions" style={{ marginTop: 6 }}>
        <button className="primary sm" type="button" onClick={onAccept} data-testid={`${testid}-accept`}>
          Accept
        </button>
        <button className="ghost sm" type="button" onClick={onReject} data-testid={`${testid}-reject`}>
          Reject
        </button>
      </div>
    </div>
  );
}
